import React, { useState, useEffect } from 'react';
import './admin-dashboard.css';
import {
  LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer
} from 'recharts';
import { HashLoader } from 'react-spinners';
import { useUser } from '../../context/UserContext';
import { showToast } from '../../components/toasts/ToastNotification';
import { getDashboardData } from './../../api/analytic.api';
import { formatVietnamesePrice, getOrderStatus } from '../../utils';

export const AdminDashboard = () => {
  const [loading, setLoading] = useState(true);
  const [totalOrders, setTotalOrders] = useState(0);
  const [totalUsers, setTotalUsers] = useState(0);
  const [revenueSummary, setRevenueSummary] = useState({});
  const [processingOrders, setProcessingOrders] = useState(0);
  const [successOrders, setSuccessOrders] = useState(0);
  const [failedOrders, setFailedOrders] = useState(0);
  const [weeklyRevenue, setWeeklyRevenue] = useState([]);
  const [ordersByStatus, setOrdersByStatus] = useState({});
  const [products, setProducts] = useState({});
  const [recentOrders, setRecentOrders] = useState([]);
  const [userGrowth, setUserGrowth] = useState([]);
  const [topDepositUsers, setTopDepositUsers] = useState([]);
  const [userGrowthSummary, setUserGrowthSummary] = useState({});
  const [totalBalance, setTotalBalance] = useState(0);

  const greetings = [
    "Chào mừng bạn trở lại!",
    "Chúc bạn một ngày làm việc hiệu quả!",
    "Hôm nay bạn muốn quản lý gì?",
    "Rất vui được gặp lại bạn!",
    "Sẵn sàng cho những nhiệm vụ mới chứ?"
  ];

  const randomGreeting = greetings[Math.floor(Math.random() * greetings.length)];

  useEffect(() => {
    document.title = 'Admin - Trang chủ';
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      const res = await getDashboardData();

      if (res.success) {
        setTotalOrders(res.data.statistics.totalOrders);
        setTotalUsers(res.data.statistics.totalUsers);
        setRevenueSummary(res.data.statistics.revenueSummary);
        setProcessingOrders(res.data.statistics.processingOrders);
        setFailedOrders(res.data.statistics.ordersByStatus.failedOrders);
        setSuccessOrders(res.data.statistics.ordersByStatus.successOrders);
        setWeeklyRevenue(res.data.statistics.weeklyRevenue);
        setOrdersByStatus(res.data.statistics.ordersByStatus);
        setProducts(res.data.statistics.productsByType);
        setRecentOrders(res.data.statistics.recentOrders);
        setUserGrowth(res.data.statistics.weeklyUserGrowth);
        setTopDepositUsers(res.data.statistics.topDepositUsers);
        setUserGrowthSummary(res.data.statistics.userGrowthSummary);
        setTotalBalance(res.data.statistics.totalBalance);
      }
    } catch (error) {
      showToast(error.message, "error");
    }
    finally {
      setLoading(false);
    }
  };

  const dailyAOVData = () => {
    return weeklyRevenue.map(item => ({
      date: item.date,
      aov: item.orders > 0 ? parseFloat((item.revenue / item.orders).toFixed(2)) : 0,
    }));
  }


  const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8'];
  const formatTooltipCurrency = (value) => {
    return value.toLocaleString('vi-VN', { style: 'currency', currency: 'VND' });
  };

  const productsCount = () => {
    return products.reduce((sum, product) => sum + (product.value || 0), 0);
  };

  if (loading) {
    return (
      <div className="loader-container">
        <HashLoader color="#092339" />
      </div>
    );
  }

  return (
    <div className="admin-dashboard-container">
      <header className="admin-dashboard-header">
        <h1>{randomGreeting}</h1>
        <div className="admin-dashboard-date-time">
          {new Date().toLocaleDateString()} | {new Date().toLocaleTimeString()}
        </div>
      </header>

      {/* Key Metrics Cards */}
      <div className="admin-dashboard-metrics-container">
        <div className="admin-dashboard-metric-card">
          <h3>Tổng đơn hàng</h3>
          <p className="admin-dashboard-metric-value">{totalOrders}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Đơn hàng thành công</h3>
          <p className="admin-dashboard-metric-value">{successOrders}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Đơn hàng bị hủy</h3>
          <p className="admin-dashboard-metric-value">{failedOrders}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Đơn hàng đang chờ</h3>
          <p className="admin-dashboard-metric-value">{processingOrders}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Doanh thu hôm nay</h3>
          <p className="admin-dashboard-metric-value">{formatVietnamesePrice(revenueSummary?.dailyRevenue)}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Doanh thu tuần</h3>
          <p className="admin-dashboard-metric-value">{formatVietnamesePrice(revenueSummary?.weeklyRevenue)}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Doanh thu tháng</h3>
          <p className="admin-dashboard-metric-value">{formatVietnamesePrice(revenueSummary?.monthlyRevenue)}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Doanh thu năm</h3>
          <p className="admin-dashboard-metric-value">{formatVietnamesePrice(revenueSummary?.yearlyRevenue)}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Tổng số  coin hiện tại</h3>
          <p className="admin-dashboard-metric-value">{`${formatVietnamesePrice(totalBalance) || 0}`}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Tổng sản phẩm</h3>
          <p className="admin-dashboard-metric-value">{productsCount()}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Tài khoản game</h3>
          <p className="admin-dashboard-metric-value">{products[0]?.value}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Gói nạp</h3>
          <p className="admin-dashboard-metric-value">{products[1]?.value}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Tiện ích</h3>
          <p className="admin-dashboard-metric-value">{products[2]?.value}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Gift Card</h3>
          <p className="admin-dashboard-metric-value">{products[3]?.value}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Tổng người dùng</h3>
          <p className="admin-dashboard-metric-value">{totalUsers}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Người dùng mới hôm nay</h3>
          <p className="admin-dashboard-metric-value">{userGrowthSummary?.dailyUsers}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Người dùng mới tuần này</h3>
          <p className="admin-dashboard-metric-value">{userGrowthSummary?.weeklyUsers}</p>
        </div>
        <div className="admin-dashboard-metric-card">
          <h3>Người dùng mới tháng này</h3>
          <p className="admin-dashboard-metric-value">{userGrowthSummary?.monthlyUsers}</p>
        </div>
      </div>

      {/* Chart Row 1 */}
      <div className="admin-dashboard-charts-row">
        <div className="admin-dashboard-chart-container">
          <h2>Doanh thu tuần</h2>
          <ResponsiveContainer width="100%" height={300}>
            <AreaChart data={weeklyRevenue}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 12 }}
                dataKey={"revenue"}
                tickFormatter={(rev) => {
                  return `${formatVietnamesePrice(rev)}`;
                }}
              />
              <Tooltip formatter={formatTooltipCurrency} />
              <Area type="monotone" dataKey="revenue" stroke="#8884d8" fill="#8884d8" />
            </AreaChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-dashboard-chart-container">
          <h2>Đơn hàng theo trạng thái</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={[
                  { name: 'Hoàn thành', value: ordersByStatus.successOrders },
                  { name: 'Đang xử lí', value: ordersByStatus.processingOrders },
                  { name: 'Đã hủy', value: ordersByStatus.failedOrders },
                ]}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {[
                  { name: 'Hoàn thành', value: ordersByStatus.successOrders },
                  { name: 'Đang xử lí', value: ordersByStatus.processingOrders },
                  { name: 'Đã hủy', value: ordersByStatus.failedOrders },
                ].map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Chart Row 2 */}
      <div className="admin-dashboard-charts-row">
        <div className="admin-dashboard-chart-container">
          <h2>Đơn hàng hằng ngày</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={weeklyRevenue}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="orders" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-dashboard-chart-container">
          <h2>Danh mục sản phẩm</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={products}
                cx="50%"
                cy="50%"
                labelLine={false}
                outerRadius={80}
                fill="#8884d8"
                dataKey="value"
                label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
              >
                {products.map((entry, index) => (
                  <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                ))}
              </Pie>
              <Tooltip />
              <Legend />
            </PieChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Chart Row 3 */}
      <div className="admin-dashboard-charts-row">
        <div className="admin-dashboard-chart-container">
          <h2>Tăng trưởng người dùng</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={userGrowth}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis tick={{ fontSize: 12 }} />
              <Tooltip />
              <Legend />
              <Bar dataKey="users" fill="#82ca9d" />
            </BarChart>
          </ResponsiveContainer>
        </div>

        <div className="admin-dashboard-chart-container">
          <h2>Giá trị đơn hàng trung bình</h2>
          <ResponsiveContainer width="100%" height={300}>
            <LineChart
              data={dailyAOVData()}
              margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="date" tick={{ fontSize: 12 }} />
              <YAxis
                tick={{ fontSize: 12 }}
                tickFormatter={(aov) => {
                  return `${formatVietnamesePrice(aov)}`;
                }} />
              <Tooltip />
              <Legend />
              <Line type="monotone" dataKey="aov" stroke="#8884d8" strokeWidth={2} activeDot={{ r: 6 }} />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Top Deposit Users Table */}
      <div className="admin-dashboard-table-container">
        <h2>Người dùng nạp tiền nhiều nhất</h2>
        <table className="admin-dashboard-orders-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Tên khách hàng</th>
              <th>Số dư</th>
              <th>Tổng đã nạp</th>
            </tr>
          </thead>
          <tbody>
            {topDepositUsers?.length > 0 ?
              topDepositUsers.map((us) => (
                <tr key={us._id}>
                  <td>{us._id}</td>
                  <td>{us.username}</td>
                  <td>{us.balance.toLocaleString()}</td>
                  <td>{us.total_amount_deposited.toLocaleString()}đ</td>
                </tr>
              ))
              :
              (
                <tr>
                  <td colSpan={4}>Không có người dùng</td>
                </tr>
              )
            }
          </tbody>
        </table>
      </div>

      {/* Recent Orders Table */}
      <div className="admin-dashboard-table-container">
        <h2>Đơn hàng gần đây</h2>
        <table className="admin-dashboard-orders-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Tên khách hàng</th>
              <th>Ngày</th>
              <th>Số tiền</th>
              <th>Trạng thái</th>
            </tr>
          </thead>
          <tbody>
            {recentOrders?.length > 0 ?
              recentOrders.map((od) => (
                <tr key={od.orderId}>
                  <td>{od.orderId}</td>
                  <td>{od.username}</td>
                  <td>{new Date(od.createdAt).toLocaleString()}</td>
                  <td>{od.amount.toLocaleString()}đ</td>
                  <td>
                    <span className={`admin-dashboard-status admin-dashboard-${getOrderStatus(od.status).class}`}>
                      {getOrderStatus(od.status).text}
                    </span>
                  </td>
                </tr>
              ))
              :
              (
                <tr>
                  <td colSpan={5}>Không có đơn hàng</td>
                </tr>
              )
            }
          </tbody>
        </table>
      </div>
    </div>
  );
};